import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Radio,
  Lock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Save,
  DownloadCloud,
  Server,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  Clock,
  Layers,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  ExternalLink,
  Globe,
  Monitor
} from 'lucide-react';

export type StrictStatus = 'Configured' | 'Not Configured' | 'Connected' | 'Connection Failed' | 'Needs Attention' | 'Needs Sign-In';

interface WellSkyState {
  isConfigured: boolean;
  hasStoredPassword?: boolean;
  username: string;
  entityId: string;
  senderId: string;
  serviceProviderContext?: string;
  focusId?: string;
  status: StrictStatus;
  statusMessage: string;
  lastSuccessfulRetrieval: string | null;
  lastRetrievalType: string | null;
  lastSourceFilenames: string[];
  lastImportedCount: number;
  lastDuplicateCount: number;
  lastHeldCount: number;
  lastErrorCount: number;
  autoNightlyEnabled?: boolean;
  nightlyScheduleTime?: string;
}

interface FocusState {
  isConfigured: boolean;
  hasStoredPassword?: boolean;
  username: string;
  status: StrictStatus;
  statusMessage: string;
  lastTestedAt: string | null;
}

interface DddSftpState {
  isConfigured: boolean;
  hasStoredPassword?: boolean;
  username: string;
  alphaCode: string;
  sftpHost: string;
  sftpPort: number;
  status: StrictStatus;
  statusMessage: string;
  lastTestedAt: string | null;
}

interface ClaimMdState {
  isConfigured: boolean;
  hasStoredPassword?: boolean;
  username: string;
  clearinghouseRoute: string;
  sftpHost: string;
  sftpPort: number;
  sftpPath: string;
  liveTransmissionEnabled: boolean;
  status: StrictStatus;
  statusMessage: string;
  lastTestedAt: string | null;
}

interface BillingIdentityState {
  companyName: string;
  legalEntityName: string;
  taxId: string;
  timeZone: string;
  npi: string;
  taxonomyCode: string;
  ahcccsId: string;
  skilledNpi: string;
  skilledAhcccsId: string;
  nonSkilledNpi: string;
  nonSkilledAhcccsId: string;
  billingContactName: string;
  billingContactAddress: string;
  city: string;
  state: string;
  zip: string;
  physicalAddress?: string;
  physicalCity?: string;
  physicalState?: string;
  physicalZip?: string;
  payToAddress?: string;
  payToCity?: string;
  payToState?: string;
  payToZip?: string;
  billingPhone: string;
  billingEmail: string;
}

const normalizeBillingIdentity = (raw: any): BillingIdentityState => ({
  companyName: raw?.companyName ?? '',
  legalEntityName: raw?.legalEntityName ?? '',
  taxId: raw?.taxId ?? '',
  timeZone: raw?.timeZone ?? '',
  npi: raw?.npi ?? '',
  taxonomyCode: raw?.taxonomyCode ?? '',
  ahcccsId: raw?.ahcccsId ?? '',
  skilledNpi: raw?.skilledNpi ?? '',
  skilledAhcccsId: raw?.skilledAhcccsId ?? '',
  nonSkilledNpi: raw?.nonSkilledNpi ?? '',
  nonSkilledAhcccsId: raw?.nonSkilledAhcccsId ?? '',
  billingContactName: raw?.billingContactName ?? '',
  billingContactAddress: raw?.billingContactAddress ?? '',
  city: raw?.city ?? '',
  state: raw?.state ?? '',
  zip: raw?.zip ?? '',
  physicalAddress: raw?.physicalAddress ?? '',
  physicalCity: raw?.physicalCity ?? '',
  physicalState: raw?.physicalState ?? '',
  physicalZip: raw?.physicalZip ?? '',
  payToAddress: raw?.payToAddress ?? '',
  payToCity: raw?.payToCity ?? '',
  payToState: raw?.payToState ?? '',
  payToZip: raw?.payToZip ?? '',
  billingPhone: raw?.billingPhone ?? '',
  billingEmail: raw?.billingEmail ?? ''
});

interface AdminConnectionsSetupViewProps {
  onNavigateToAuthorizations?: () => void;
}

export const AdminConnectionsSetupView: React.FC<AdminConnectionsSetupViewProps> = ({
  onNavigateToAuthorizations
}) => {
  type SectionKey = 'wellsky' | 'focus' | 'ddd-sftp' | 'claimmd' | 'identity';
  const [expandedSection, setExpandedSection] = useState<SectionKey | null>(null);

  const toggleSection = (section: SectionKey) => {
    setExpandedSection(prev => (prev === section ? null : section));
  };

  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [retrieving, setRetrieving] = useState(false);
  const [retrievalResult, setRetrievalResult] = useState<any | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // 1. WellSky form inputs
  const [wsUser, setWsUser] = useState('');
  const [wsPass, setWsPass] = useState('');
  const [wsEntityId, setWsEntityId] = useState('');
  const [wsSenderId, setWsSenderId] = useState('');
  const [wsData, setWsData] = useState<WellSkyState>({
    isConfigured: false,
    username: '',
    entityId: '',
    senderId: '',
    status: 'Not Configured',
    statusMessage: 'Not Configured',
    lastSuccessfulRetrieval: null,
    lastRetrievalType: null,
    lastSourceFilenames: [],
    lastImportedCount: 0,
    lastDuplicateCount: 0,
    lastHeldCount: 0,
    lastErrorCount: 0
  });

  // 2. FOCUS form inputs
  const [focusUser, setFocusUser] = useState('');
  const [focusPass, setFocusPass] = useState('');
  const [focusData, setFocusData] = useState<FocusState>({
    isConfigured: false,
    username: '',
    status: 'Not Configured',
    statusMessage: 'Not Configured',
    lastTestedAt: null
  });

  // 3. DDD SFTP form inputs
  const [sftpUser, setSftpUser] = useState('');
  const [sftpPass, setSftpPass] = useState('');
  const [sftpAlpha, setSftpAlpha] = useState('');
  const [sftpHost, setSftpHost] = useState('sftp.azdes.gov');
  const [sftpPort, setSftpPort] = useState(22);
  const [sftpData, setSftpData] = useState<DddSftpState>({
    isConfigured: false,
    username: '',
    alphaCode: '',
    sftpHost: 'sftp.azdes.gov',
    sftpPort: 22,
    status: 'Not Configured',
    statusMessage: 'Not Configured',
    lastTestedAt: null
  });

  // 4. Claim.MD form inputs
  const [cmdUser, setCmdUser] = useState('');
  const [cmdPass, setCmdPass] = useState('');
  const [cmdHost, setCmdHost] = useState('sftp.claim.md');
  const [cmdPort, setCmdPort] = useState(22);
  const [cmdPath, setCmdPath] = useState('/inbound');
  const [showAdvancedCmd, setShowAdvancedCmd] = useState(false);
  const [cmdData, setCmdData] = useState<ClaimMdState>({
    isConfigured: false,
    username: '',
    clearinghouseRoute: 'Claim.MD (Active)',
    sftpHost: 'sftp.claim.md',
    sftpPort: 22,
    sftpPath: '/inbound',
    liveTransmissionEnabled: false,
    status: 'Not Configured',
    statusMessage: 'Not Configured',
    lastTestedAt: null
  });

  // Password Visibility States (Default masked)
  const [showWsPass, setShowWsPass] = useState(false);
  const [showFocusPass, setShowFocusPass] = useState(false);
  const [showSftpPass, setShowSftpPass] = useState(false);
  const [showCmdPass, setShowCmdPass] = useState(false);
  const [revealingService, setRevealingService] = useState<string | null>(null);

  // 5. Company Billing Identity form inputs
  const [identity, setIdentity] = useState<BillingIdentityState>({
    companyName: 'Allegiant Therapy LLC',
    legalEntityName: 'Allegiant Therapy LLC',
    taxId: '',
    timeZone: 'America/Phoenix (MST / No DST)',
    npi: '1124738109',
    taxonomyCode: '235Z00000X',
    ahcccsId: '',
    skilledNpi: '',
    skilledAhcccsId: '',
    nonSkilledNpi: '',
    nonSkilledAhcccsId: '',
    billingContactName: 'Billing Department',
    billingContactAddress: 'P.O. Box 513',
    city: 'Laveen',
    state: 'AZ',
    zip: '85339',
    physicalAddress: '',
    physicalCity: '',
    physicalState: '',
    physicalZip: '',
    payToAddress: '',
    payToCity: '',
    payToState: '',
    payToZip: '',
    billingPhone: '',
    billingEmail: 'billing@allegianttherapy.com'
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/connections');
      if (!res.ok) throw new Error('Failed to load connection configurations');
      const data = await res.json();

      // WellSky
      setWsData(data.wellsky);
      setWsUser(data.wellsky.username || '');
      setWsEntityId(data.wellsky.entityId || '');
      setWsSenderId(data.wellsky.senderId || '');

      // FOCUS
      setFocusData(data.focus);
      setFocusUser(data.focus.username || '');

      // DDD SFTP
      setSftpData(data.dddSftp);
      setSftpUser(data.dddSftp.username || '');
      setSftpAlpha(data.dddSftp.alphaCode || '');
      setSftpHost(data.dddSftp.sftpHost || 'sftp.azdes.gov');
      setSftpPort(data.dddSftp.sftpPort || 22);

      // Claim.MD
      setCmdData(data.claimMd);
      setCmdUser(data.claimMd.username || '');
      setCmdHost(data.claimMd.sftpHost || 'sftp.claim.md');
      setCmdPort(data.claimMd.sftpPort || 22);
      setCmdPath(data.claimMd.sftpPath || '/inbound');

      // Identity
      if (data.billingIdentity) {
        setIdentity(normalizeBillingIdentity(data.billingIdentity));
      }
    } catch (err: any) {
      showToast(err.message || 'Error fetching connections', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // --- ACTIONS ---

  const handleToggleReveal = async (service: 'wellsky' | 'focus' | 'ddd-sftp' | 'claimmd') => {
    if (service === 'wellsky') {
      if (showWsPass) {
        setShowWsPass(false);
        return;
      }
      if (!wsPass && wsData.hasStoredPassword) {
        try {
          setRevealingService('wellsky');
          const res = await fetch('/api/connections/wellsky/reveal-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Failed to reveal password');
          setWsPass(data.password);
          setShowWsPass(true);
          showToast('WellSky password revealed from secure vault.', 'info');
        } catch (err: any) {
          showToast(err.message, 'error');
        } finally {
          setRevealingService(null);
        }
      } else {
        setShowWsPass(true);
      }
    } else if (service === 'focus') {
      if (showFocusPass) {
        setShowFocusPass(false);
        return;
      }
      if (!focusPass && focusData.hasStoredPassword) {
        try {
          setRevealingService('focus');
          const res = await fetch('/api/connections/focus/reveal-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Failed to reveal password');
          setFocusPass(data.password);
          setShowFocusPass(true);
          showToast('FOCUS password revealed from secure vault.', 'info');
        } catch (err: any) {
          showToast(err.message, 'error');
        } finally {
          setRevealingService(null);
        }
      } else {
        setShowFocusPass(true);
      }
    } else if (service === 'ddd-sftp') {
      if (showSftpPass) {
        setShowSftpPass(false);
        return;
      }
      if (!sftpPass && sftpData.hasStoredPassword) {
        try {
          setRevealingService('ddd-sftp');
          const res = await fetch('/api/connections/ddd-sftp/reveal-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Failed to reveal password');
          setSftpPass(data.password);
          setShowSftpPass(true);
          showToast('DDD SFTP password revealed from secure vault.', 'info');
        } catch (err: any) {
          showToast(err.message, 'error');
        } finally {
          setRevealingService(null);
        }
      } else {
        setShowSftpPass(true);
      }
    } else if (service === 'claimmd') {
      if (showCmdPass) {
        setShowCmdPass(false);
        return;
      }
      if (!cmdPass && cmdData.hasStoredPassword) {
        try {
          setRevealingService('claimmd');
          const res = await fetch('/api/connections/claimmd/reveal-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Failed to reveal password');
          setCmdPass(data.password);
          setShowCmdPass(true);
          showToast('Claim.MD password revealed from secure vault.', 'info');
        } catch (err: any) {
          showToast(err.message, 'error');
        } finally {
          setRevealingService(null);
        }
      } else {
        setShowCmdPass(true);
      }
    }
  };

  // 1. WellSky
  const handleSaveWellSky = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wsUser.trim()) {
      showToast('WellSky Username is required', 'error');
      return;
    }
    if (!wsPass.trim() && !wsData.isConfigured) {
      showToast('WellSky Password is required for initial configuration', 'error');
      return;
    }
    try {
      setSavingKey('wellsky');
      const res = await fetch('/api/connections/wellsky', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: wsUser.trim(),
          password: wsPass.trim() || undefined,
          entityId: wsEntityId.trim(),
          senderId: wsSenderId.trim()
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to save WellSky credentials');
      setWsData(result.config);
      setWsPass(''); // Never keep password in memory after save
      setShowWsPass(false);
      showToast('WellSky credentials saved securely server-side.', 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleTestWellSky = async () => {
    try {
      setTestingKey('wellsky');
      const res = await fetch('/api/connections/wellsky/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'WellSky connection test failed');
      setWsData(prev => ({
        ...prev,
        status: result.status,
        statusMessage: result.message
      }));
      showToast(result.message, result.success ? 'success' : 'error');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setTestingKey(null);
    }
  };

  // 2. FOCUS
  const handleSaveFocus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!focusUser.trim()) {
      showToast('FOCUS Username is required', 'error');
      return;
    }
    if (!focusPass.trim() && !focusData.isConfigured) {
      showToast('FOCUS Password is required for initial configuration', 'error');
      return;
    }
    try {
      setSavingKey('focus');
      const res = await fetch('/api/connections/focus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: focusUser.trim(),
          password: focusPass.trim() || undefined
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to save FOCUS credentials');
      setFocusData(result.config);
      setFocusPass('');
      setShowFocusPass(false);
      showToast('FOCUS credentials saved securely server-side.', 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleTestFocus = async () => {
    try {
      setTestingKey('focus');
      const res = await fetch('/api/connections/focus/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'FOCUS test failed');
      setFocusData(prev => ({
        ...prev,
        status: result.status,
        statusMessage: result.message
      }));
      showToast(result.message, result.success ? 'success' : 'error');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setTestingKey(null);
    }
  };

  // 3. DDD SFTP
  const handleSaveDddSftp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sftpUser.trim()) {
      showToast('DDD SFTP Username is required', 'error');
      return;
    }
    if (!sftpPass.trim() && !sftpData.isConfigured) {
      showToast('DDD SFTP Password is required for initial configuration', 'error');
      return;
    }
    try {
      setSavingKey('dddSftp');
      const res = await fetch('/api/connections/ddd-sftp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: sftpUser.trim(),
          password: sftpPass.trim() || undefined,
          alphaCode: sftpAlpha.trim(),
          sftpHost: sftpHost.trim(),
          sftpPort: Number(sftpPort) || 22
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to save DDD SFTP credentials');
      setSftpData(result.config);
      setSftpPass('');
      setShowSftpPass(false);
      showToast('DDD SFTP credentials saved securely server-side.', 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleTestDddSftp = async () => {
    try {
      setTestingKey('dddSftp');
      const res = await fetch('/api/connections/ddd-sftp/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'DDD SFTP test failed');
      setSftpData(prev => ({
        ...prev,
        status: result.status,
        statusMessage: result.message
      }));
      showToast(result.message, result.success ? 'success' : 'error');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setTestingKey(null);
    }
  };

  // 4. Claim.MD
  const handleSaveClaimMd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cmdUser.trim()) {
      showToast('Claim.MD / Clearinghouse Username is required', 'error');
      return;
    }
    if (!cmdPass.trim() && !cmdData.isConfigured) {
      showToast('Claim.MD Password is required for initial configuration', 'error');
      return;
    }
    try {
      setSavingKey('claimMd');
      const res = await fetch('/api/connections/claimmd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: cmdUser.trim(),
          password: cmdPass.trim() || undefined,
          sftpHost: cmdHost.trim(),
          sftpPort: Number(cmdPort) || 22,
          sftpPath: cmdPath.trim()
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to save Claim.MD credentials');
      setCmdData(result.config);
      setCmdPass('');
      setShowCmdPass(false);
      showToast('Claim.MD clearinghouse credentials saved. Live transmission gated OFF.', 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleTestClaimMd = async () => {
    try {
      setTestingKey('claimMd');
      const res = await fetch('/api/connections/claimmd/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Claim.MD test failed');
      setCmdData(prev => ({
        ...prev,
        status: result.status,
        statusMessage: result.message
      }));
      showToast(result.message, result.success ? 'success' : 'error');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setTestingKey(null);
    }
  };

  // 5. Company Identity
  const handleSaveIdentity = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingKey('identity');
      const res = await fetch('/api/connections/billing-identity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(identity)
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to save billing identity');
      if (result.identity) {
        setIdentity(normalizeBillingIdentity(result.identity));
      }
      showToast('Company billing identity updated successfully.', 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  // Status Badge Component strictly adhering to permitted status values
  const renderStatusBadge = (status: StrictStatus) => {
    switch (status) {
      case 'Connected':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-bold font-mono bg-emerald-50 text-emerald-700 border border-emerald-300">
            <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />
            Connected
          </span>
        );
      case 'Configured':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-bold font-mono bg-sky-50 text-sky-700 border border-sky-300">
            <Check className="w-3 h-3 mr-1 text-sky-600" />
            Configured
          </span>
        );
      case 'Needs Sign-In':
      case 'Needs Attention':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-bold font-mono bg-amber-50 text-amber-700 border border-amber-300">
            <AlertTriangle className="w-3 h-3 mr-1 text-amber-600" />
            {status}
          </span>
        );
      case 'Connection Failed':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-bold font-mono bg-rose-50 text-rose-700 border border-rose-300">
            <XCircle className="w-3 h-3 mr-1 text-rose-600" />
            Connection Failed
          </span>
        );
      case 'Not Configured':
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-bold font-mono bg-amber-50 text-amber-700 border border-amber-300">
            <AlertTriangle className="w-3 h-3 mr-1 text-amber-600" />
            Not Configured
          </span>
        );
    }
  };

  return (
    <div className="space-y-3 pb-12">
      {/* Toast feedback */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-3.5 py-2 rounded-lg shadow-xl border text-xs font-medium flex items-center space-x-2 transition-all ${
            toast.type === 'success'
              ? 'bg-slate-900 text-white border-emerald-600'
              : toast.type === 'error'
              ? 'bg-slate-900 text-white border-rose-600'
              : 'bg-slate-900 text-white border-slate-700'
          }`}
        >
          {toast.type === 'success' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
          {toast.type === 'error' && <XCircle className="w-3.5 h-3.5 text-rose-400" />}
          {toast.type === 'info' && <Radio className="w-3.5 h-3.5 text-slate-300" />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Mobile Screen Title & Header (sm:hidden) */}
      <div className="flex sm:hidden flex-wrap items-center justify-between gap-2 border-b border-slate-800/80 pb-2.5">
        <div className="flex items-center space-x-2.5">
          <div className="p-1.5 rounded-lg bg-slate-800 text-slate-300 border border-slate-700 shadow-sm">
            <Radio className="w-4 h-4 text-slate-300" />
          </div>
          <h1 className="text-lg font-bold text-white tracking-tight">
            Connections & Billing Setup
          </h1>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 font-medium leading-none">
            Therapy Corner Port
          </span>
        </div>

        <button
          onClick={fetchAll}
          disabled={loading}
          className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-sm cursor-pointer disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 text-slate-300 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh All Status</span>
        </button>
      </div>

      {/* Desktop/Tablet Header Shell: Deep slate/navy structural header */}
      <div className="hidden sm:block relative overflow-hidden rounded-2xl border border-slate-800 bg-[#0f172a] px-6 py-4 shadow-sm">
        {/* Subtle top hairline */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-slate-800 pointer-events-none" />

        <div className="relative z-10 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 rounded-lg bg-slate-800 text-slate-300 border border-slate-700 shadow-sm">
              <Radio className="w-4 h-4 text-slate-300" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">
              Connections & Billing Setup
            </h1>
            <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 font-medium leading-none">
              Therapy Corner Port
            </span>
            <span className="text-xs text-slate-400 font-mono hidden md:inline border-l border-slate-800 pl-2.5">
              AES-256 Vault
            </span>
          </div>

          <button
            onClick={fetchAll}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white border border-slate-700 text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-300 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh All Status</span>
          </button>
        </div>
      </div>

      {/* Architecture & Route Status Tiles - Compact Quick Status Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs">
        {/* WellSky Route */}
        <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[9.5px] font-mono uppercase text-slate-600 font-bold tracking-wider">WellSky</span>
            <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-medium">
              FOCUS ID: 115157
            </span>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs font-semibold text-slate-800">DDD Operations</span>
            {renderStatusBadge(wsData.status)}
          </div>
        </div>

        {/* FOCUS Route */}
        <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[9.5px] font-mono uppercase text-slate-600 font-bold tracking-wider">FOCUS</span>
            <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-medium">
              DDD Payer
            </span>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs font-semibold text-slate-800">Payer Gateway</span>
            {renderStatusBadge(focusData.status)}
          </div>
        </div>

        {/* Claim.MD Route */}
        <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[9.5px] font-mono uppercase text-slate-600 font-bold tracking-wider">Claim.MD</span>
            <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
              Active
            </span>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs font-semibold text-slate-800">Clearinghouse</span>
            {renderStatusBadge(cmdData.status)}
          </div>
        </div>

        {/* Claim Transmission Gate */}
        <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[9.5px] font-mono uppercase text-slate-600 font-bold tracking-wider">Safety Gate</span>
            <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-bold flex items-center gap-1">
              <Lock className="w-2.5 h-2.5" />
              GATED OFF
            </span>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs font-semibold text-slate-800">Claim Transmission</span>
            <span className="text-[10px] font-mono text-amber-700 font-semibold">Audit Safe</span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* CONNECTION GATEWAYS (ROWS 1–4) — FLATTENED OPERATIONAL TABLE */}
      {/* ========================================================================= */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-200 overflow-hidden">
        {/* ========================================================================= */}
        {/* 1. WELLSKY CONNECTION (Manual Report 11281 Source File Model) */}
        {/* ========================================================================= */}
        <div className={`relative transition-colors duration-150 border-l-4 ${
          expandedSection === 'wellsky'
            ? 'border-l-slate-800 bg-slate-50/50'
            : 'border-l-transparent hover:border-l-slate-300 bg-white hover:bg-slate-50/30'
        }`}>
          {/* Collapsed Header / Clickable Summary Row */}
          <div
            onClick={() => toggleSection('wellsky')}
            className="group relative flex flex-wrap items-center justify-between gap-2.5 px-4 py-3 cursor-pointer select-none transition-colors"
          >
            <div className="relative z-10 flex items-center flex-wrap gap-2.5 min-w-0">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10.5px] font-bold font-mono border border-slate-200 shrink-0">
                1
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-slate-900 text-xs tracking-tight">WellSky</span>
                <span className="text-slate-600 text-xs font-normal">— DDD Operational Source</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-mono">
                  Report 11281 Manual Ingestion
                </span>
              </div>
            </div>

            <div className="relative z-10 flex items-center flex-wrap gap-2.5 ml-auto">
              <div className="hidden sm:flex items-center space-x-1.5 text-[11px] font-mono text-slate-600">
                <span>Entity: <span className="text-slate-900 font-semibold">{wsEntityId || wsData.entityId || '359577'}</span></span>
                <span className="text-slate-300">•</span>
                <span>FOCUS: <span className="text-slate-900 font-semibold">115157</span></span>
              </div>

              <div>{renderStatusBadge(wsData.status)}</div>

              <div className={`px-2 py-0.5 rounded text-[11px] font-semibold flex items-center space-x-1 border transition-all ${
                expandedSection === 'wellsky'
                  ? 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900 hover:bg-slate-100'
              }`}>
                <span>{expandedSection === 'wellsky' ? 'Close' : 'Edit'}</span>
                {expandedSection === 'wellsky' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {expandedSection === 'wellsky' && (
            <div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-slate-50/40 space-y-3">

        {/* Ingestion & Telemetry Strip - Compact */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-[11px]">
          <div>
            <div className="text-[9px] font-mono text-slate-600 uppercase leading-none">Operational Source Model</div>
            <div className="font-mono text-slate-800 text-[11px] truncate mt-0.5 font-bold">
              Manual Portal Export → Everything App Ingestion
            </div>
          </div>
          <div>
            <div className="text-[9px] font-mono text-slate-600 uppercase leading-none">Audited Allegiant Context</div>
            <div className="font-semibold text-slate-800 text-[11px] mt-0.5 flex items-center space-x-1">
              <span className="text-slate-700">FOCUS ID: 115157</span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-700">Vendor ID: 359577</span>
            </div>
          </div>
          <div>
            <div className="text-[9px] font-mono text-slate-600 uppercase leading-none">Report Scope</div>
            <div className="font-mono text-slate-700 text-[11px] mt-0.5 truncate">
              Providers → Provider Authorizations (11281)
            </div>
          </div>
        </div>

        <form onSubmit={handleSaveWellSky} className="space-y-2.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                WellSky Username <span className="text-slate-400">*</span>
              </label>
              <input
                id="wellsky-username-input"
                type="text"
                value={wsUser}
                onChange={(e) => setWsUser(e.target.value)}
                placeholder="Enter WellSky username"
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                WellSky Password <span className="text-slate-400">*</span>
              </label>
              <div className="relative">
                <input
                  id="wellsky-password-input"
                  type={showWsPass ? 'text' : 'password'}
                  value={wsPass}
                  onChange={(e) => setWsPass(e.target.value)}
                  placeholder={wsData.isConfigured ? '•••••••••••••••• (configured)' : 'Enter password'}
                  className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                />
                <button
                  type="button"
                  id="toggle-wellsky-password-visibility"
                  onClick={() => handleToggleReveal('wellsky')}
                  disabled={revealingService === 'wellsky'}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5 rounded transition-colors disabled:opacity-50"
                  title={showWsPass ? 'Hide password' : 'Show / Reveal stored password'}
                >
                  {revealingService === 'wellsky' ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-500" />
                  ) : showWsPass ? (
                    <EyeOff className="w-3.5 h-3.5 text-slate-600" />
                  ) : (
                    <Eye className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                WellSky Entity ID
              </label>
              <input
                id="wellsky-entity-id-input"
                type="text"
                value={wsEntityId}
                onChange={(e) => setWsEntityId(e.target.value)}
                placeholder="359577"
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                WellSky Sender ID
              </label>
              <input
                id="wellsky-sender-id-input"
                type="text"
                value={wsSenderId}
                onChange={(e) => setWsSenderId(e.target.value)}
                placeholder="359577_Alleg"
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 font-mono"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200">
            <div className="text-[10px] text-slate-600 font-mono truncate">
              Provider Context: <span className="text-slate-800 font-semibold">Allegiant Therapy</span> • FOCUS ID: <span className="text-slate-800 font-semibold">115157</span> • Entity: <span className="text-slate-800 font-semibold">359577</span> • Sender: <span className="text-slate-800 font-semibold">359577_Alleg</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="submit"
                id="btn-save-wellsky"
                disabled={savingKey === 'wellsky'}
                className="px-3 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
              >
                <Save className="w-3 h-3" />
                <span>{savingKey === 'wellsky' ? 'Saving...' : 'Save'}</span>
              </button>

              <button
                type="button"
                id="btn-test-wellsky"
                onClick={handleTestWellSky}
                disabled={testingKey === 'wellsky'}
                className="px-2.5 py-1 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-medium flex items-center space-x-1.5 transition-all shadow-sm cursor-pointer disabled:opacity-50"
              >
                <CheckCircle2 className="w-3 h-3 text-slate-500" />
                <span>{testingKey === 'wellsky' ? 'Testing...' : 'Test Connection'}</span>
              </button>

              {onNavigateToAuthorizations && (
                <button
                  type="button"
                  id="btn-open-ingestion"
                  onClick={onNavigateToAuthorizations}
                  className="px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer"
                >
                  <DownloadCloud className="w-3.5 h-3.5" />
                  <span>Import Report 11281 CSV</span>
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Live Retrieval Telemetry if available */}
        {wsData.lastSuccessfulRetrieval && (
          <div className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-xs space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-2 text-slate-600 text-[10.5px]">
              <div>
                Last Retrieval: <span className="text-slate-800 font-mono font-semibold">{wsData.lastSuccessfulRetrieval}</span>
                {wsData.lastRetrievalType && (
                  <span className="ml-1.5 font-mono text-[9.5px] px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200">
                    [{wsData.lastRetrievalType}]
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2.5 text-[10.5px] font-mono">
                <span className="text-emerald-700 font-bold">{wsData.lastImportedCount} Imported</span>
                <span className="text-slate-600">{wsData.lastDuplicateCount} Duplicates</span>
                <span className="text-amber-700 font-semibold">{wsData.lastHeldCount} Held</span>
                {wsData.lastErrorCount > 0 && <span className="text-rose-700 font-semibold">{wsData.lastErrorCount} Errors</span>}
              </div>
            </div>
            {wsData.lastSourceFilenames && wsData.lastSourceFilenames.length > 0 && (
              <div className="text-[10px] font-mono text-slate-600 flex items-center space-x-1.5 pt-1 border-t border-slate-100">
                <FileText className="w-3 h-3 text-slate-400" />
                <span>Source File:</span>
                <span className="text-slate-700 font-semibold">{wsData.lastSourceFilenames.join(', ')}</span>
              </div>
            )}
          </div>
        )}
          </div>
        )}
        </div>

        {/* ========================================================================= */}
        {/* 2. FOCUS CONNECTION */}
        {/* ========================================================================= */}
        <div className={`relative transition-colors duration-150 border-l-4 ${
          expandedSection === 'focus'
            ? 'border-l-slate-800 bg-slate-50/50'
            : 'border-l-transparent hover:border-l-slate-300 bg-white hover:bg-slate-50/30'
        }`}>
          {/* Collapsed Header / Clickable Summary Row */}
          <div
            onClick={() => toggleSection('focus')}
            className="group relative flex flex-wrap items-center justify-between gap-2.5 px-4 py-3 cursor-pointer select-none transition-colors"
          >
            <div className="relative z-10 flex items-center flex-wrap gap-2.5 min-w-0">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10.5px] font-bold font-mono border border-slate-200 shrink-0">
                2
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-slate-900 text-xs tracking-tight">FOCUS</span>
                <span className="text-slate-600 text-xs font-normal">— DDD Payer-Side Gateway</span>
              </div>
            </div>

            <div className="relative z-10 flex items-center flex-wrap gap-2.5 ml-auto">
              <div className="hidden sm:flex items-center space-x-1.5 text-[11px] font-mono text-slate-600">
                <span>FOCUS ID: <span className="text-slate-900 font-semibold">115157</span></span>
                <span className="text-slate-300">•</span>
                <span className="text-slate-600">Payer Gateway</span>
              </div>

              <div>{renderStatusBadge(focusData.status)}</div>

              <div className={`px-2 py-0.5 rounded text-[11px] font-semibold flex items-center space-x-1 border transition-all ${
                expandedSection === 'focus'
                  ? 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900 hover:bg-slate-100'
              }`}>
                <span>{expandedSection === 'focus' ? 'Close' : 'Edit'}</span>
                {expandedSection === 'focus' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {expandedSection === 'focus' && (
            <div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-slate-50/40 space-y-3">
            <form onSubmit={handleSaveFocus} className="space-y-2.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    FOCUS Username <span className="text-slate-400">*</span>
                  </label>
                  <input
                    id="focus-username-input"
                    type="text"
                    value={focusUser}
                    onChange={(e) => setFocusUser(e.target.value)}
                    placeholder="Enter FOCUS username"
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    FOCUS Password <span className="text-slate-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="focus-password-input"
                      type={showFocusPass ? 'text' : 'password'}
                      value={focusPass}
                      onChange={(e) => setFocusPass(e.target.value)}
                      placeholder={focusData.isConfigured ? '•••••••••••••••• (configured)' : 'Enter password'}
                      className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                    />
                    <button
                      type="button"
                      id="toggle-focus-password-visibility"
                      onClick={() => handleToggleReveal('focus')}
                      disabled={revealingService === 'focus'}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5 rounded transition-colors disabled:opacity-50"
                      title={showFocusPass ? 'Hide password' : 'Show / Reveal stored password'}
                    >
                      {revealingService === 'focus' ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-500" />
                      ) : showFocusPass ? (
                        <EyeOff className="w-3.5 h-3.5 text-slate-600" />
                      ) : (
                        <Eye className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200">
                <div className="text-[10px] text-slate-600 font-mono">
                  FOCUS ID: <span className="text-slate-900 font-semibold">115157</span> • Provider: <span className="text-slate-800 font-semibold">Allegiant Therapy</span> • <span className="text-amber-700 font-semibold">Distinct from WellSky 359577</span>
                  {focusData.lastTestedAt && <span className="ml-1.5 text-slate-600 font-normal">({`Tested: ${focusData.lastTestedAt}`})</span>}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="submit"
                    id="btn-save-focus"
                    disabled={savingKey === 'focus'}
                    className="px-3 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
                  >
                    <Save className="w-3 h-3" />
                    <span>{savingKey === 'focus' ? 'Saving...' : 'Save'}</span>
                  </button>

                  <button
                    type="button"
                    id="btn-test-focus"
                    onClick={handleTestFocus}
                    disabled={testingKey === 'focus'}
                    className="px-2.5 py-1 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-medium flex items-center space-x-1.5 transition-all shadow-sm cursor-pointer disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-3 h-3 text-slate-500" />
                    <span>{testingKey === 'focus' ? 'Testing...' : 'Test Connection'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        </div>

        {/* ========================================================================= */}
        {/* 3. DDD SFTP CONNECTION */}
        {/* ========================================================================= */}
        <div className={`relative transition-colors duration-150 border-l-4 ${
          expandedSection === 'ddd-sftp'
            ? 'border-l-slate-800 bg-slate-50/50'
            : 'border-l-transparent hover:border-l-slate-300 bg-white hover:bg-slate-50/30'
        }`}>
          {/* Collapsed Header / Clickable Summary Row */}
          <div
            onClick={() => toggleSection('ddd-sftp')}
            className="group relative flex flex-wrap items-center justify-between gap-2.5 px-4 py-3 cursor-pointer select-none transition-colors"
          >
            <div className="relative z-10 flex items-center flex-wrap gap-2.5 min-w-0">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10.5px] font-bold font-mono border border-slate-200 shrink-0">
                3
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-slate-900 text-xs tracking-tight">DDD SFTP</span>
                <span className="text-slate-600 text-xs font-normal">— Direct Secure Transport</span>
              </div>
            </div>

            <div className="relative z-10 flex items-center flex-wrap gap-2.5 ml-auto">
              <div className="hidden sm:flex items-center space-x-1.5 text-[11px] font-mono text-slate-600">
                <span>Alpha: <span className="text-slate-900 font-semibold">{sftpAlpha || 'ALGT'}</span></span>
                <span className="text-slate-300">•</span>
                <span>Host: <span className="text-slate-700">{sftpHost}:{sftpPort}</span></span>
              </div>

              <div>{renderStatusBadge(sftpData.status)}</div>

              <div className={`px-2 py-0.5 rounded text-[11px] font-semibold flex items-center space-x-1 border transition-all ${
                expandedSection === 'ddd-sftp'
                  ? 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900 hover:bg-slate-100'
              }`}>
                <span>{expandedSection === 'ddd-sftp' ? 'Close' : 'Edit'}</span>
                {expandedSection === 'ddd-sftp' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {expandedSection === 'ddd-sftp' && (
            <div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-slate-50/40 space-y-3">
            <form onSubmit={handleSaveDddSftp} className="space-y-2.5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    DDD SFTP Username <span className="text-slate-400">*</span>
                  </label>
                  <input
                    id="ddd-sftp-username-input"
                    type="text"
                    value={sftpUser}
                    onChange={(e) => setSftpUser(e.target.value)}
                    placeholder="Enter DDD SFTP username"
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    DDD SFTP Password <span className="text-slate-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="ddd-sftp-password-input"
                      type={showSftpPass ? 'text' : 'password'}
                      value={sftpPass}
                      onChange={(e) => setSftpPass(e.target.value)}
                      placeholder={sftpData.isConfigured ? '•••••••••••••••• (configured)' : 'Enter password'}
                      className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                    />
                    <button
                      type="button"
                      id="toggle-ddd-sftp-password-visibility"
                      onClick={() => handleToggleReveal('ddd-sftp')}
                      disabled={revealingService === 'ddd-sftp'}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5 rounded transition-colors disabled:opacity-50"
                      title={showSftpPass ? 'Hide password' : 'Show / Reveal stored password'}
                    >
                      {revealingService === 'ddd-sftp' ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-500" />
                      ) : showSftpPass ? (
                        <EyeOff className="w-3.5 h-3.5 text-slate-600" />
                      ) : (
                        <Eye className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    DDD Alpha Code
                  </label>
                  <input
                    id="ddd-alpha-code-input"
                    type="text"
                    value={sftpAlpha}
                    onChange={(e) => setSftpAlpha(e.target.value.toUpperCase())}
                    placeholder="ALGT"
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 font-mono uppercase"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200">
                <div className="text-[10px] text-slate-600 font-mono">
                  Arizona DDD SFTP • Alpha Code: <span className="text-slate-900 font-semibold">ALGT</span> • Endpoint: <span className="text-slate-700">{sftpHost}:{sftpPort}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="submit"
                    id="btn-save-ddd-sftp"
                    disabled={savingKey === 'dddSftp'}
                    className="px-3 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
                  >
                    <Save className="w-3 h-3" />
                    <span>{savingKey === 'dddSftp' ? 'Saving...' : 'Save'}</span>
                  </button>

                  <button
                    type="button"
                    id="btn-test-ddd-sftp"
                    onClick={handleTestDddSftp}
                    disabled={testingKey === 'dddSftp'}
                    className="px-2.5 py-1 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-medium flex items-center space-x-1.5 transition-all shadow-sm cursor-pointer disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-3 h-3 text-slate-500" />
                    <span>{testingKey === 'dddSftp' ? 'Testing...' : 'Test SFTP Connection'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        </div>

        {/* ========================================================================= */}
        {/* 4. CLAIM.MD / CLEARINGHOUSE CONNECTION */}
        {/* ========================================================================= */}
        <div className={`relative transition-colors duration-150 border-l-4 ${
          expandedSection === 'claimmd'
            ? 'border-l-slate-800 bg-slate-50/50'
            : 'border-l-transparent hover:border-l-slate-300 bg-white hover:bg-slate-50/30'
        }`}>
          {/* Collapsed Header / Clickable Summary Row */}
          <div
            onClick={() => toggleSection('claimmd')}
            className="group relative flex flex-wrap items-center justify-between gap-2.5 px-4 py-3 cursor-pointer select-none transition-colors"
          >
            <div className="relative z-10 flex items-center flex-wrap gap-2.5 min-w-0">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10.5px] font-bold font-mono border border-slate-200 shrink-0">
                4
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-slate-900 text-xs tracking-tight">Claim.MD</span>
                <span className="text-slate-600 text-xs font-normal">— Active Clearinghouse Route</span>
              </div>
            </div>

            <div className="relative z-10 flex items-center flex-wrap gap-2.5 ml-auto">
              <div className="hidden sm:flex items-center space-x-1.5 text-[11px] font-mono text-slate-600">
                <span className="text-amber-700 font-semibold flex items-center gap-1">
                  <Lock className="w-3 h-3 text-amber-600" />
                  Transmission Gated OFF
                </span>
                <span className="text-slate-300">•</span>
                <span>Submitter: <span className="text-slate-900 font-semibold">21119</span></span>
              </div>

              <div>{renderStatusBadge(cmdData.status)}</div>

              <div className={`px-2 py-0.5 rounded text-[11px] font-semibold flex items-center space-x-1 border transition-all ${
                expandedSection === 'claimmd'
                  ? 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900 hover:bg-slate-100'
              }`}>
                <span>{expandedSection === 'claimmd' ? 'Close' : 'Edit'}</span>
                {expandedSection === 'claimmd' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {expandedSection === 'claimmd' && (
            <div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-slate-50/40 space-y-3">

        {/* Safety Gate Alert - Compact High Visibility */}
        <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center space-x-2 min-w-0">
            <div className="p-1 rounded bg-amber-100 border border-amber-300 shrink-0">
              <Lock className="w-3.5 h-3.5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-amber-900 text-xs">
                  Claim Transmission Safety Gate:
                </span>
                <span className="font-mono text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                  HARD-GATED OFF
                </span>
              </div>
              <p className="text-slate-600 text-[10.5px] leading-tight truncate hidden sm:block">
                Configuring credentials prepares transport gateway; live external claim transmission remains strictly OFF until ANSI ASC X12 837P compile validation is audited.
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-amber-800 font-semibold whitespace-nowrap hidden lg:inline">
            ANSI 837P Compile Protected
          </span>
        </div>

        <form onSubmit={handleSaveClaimMd} className="space-y-2.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Claim.MD / Clearinghouse Username <span className="text-slate-400">*</span>
              </label>
              <input
                id="claimmd-username-input"
                type="text"
                value={cmdUser}
                onChange={(e) => setCmdUser(e.target.value)}
                placeholder="Enter Claim.MD username"
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Claim.MD / Clearinghouse Password <span className="text-slate-400">*</span>
              </label>
              <div className="relative">
                <input
                  id="claimmd-password-input"
                  type={showCmdPass ? 'text' : 'password'}
                  value={cmdPass}
                  onChange={(e) => setCmdPass(e.target.value)}
                  placeholder={cmdData.isConfigured ? '•••••••••••••••• (configured)' : 'Enter password'}
                  className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                />
                <button
                  type="button"
                  id="toggle-claimmd-password-visibility"
                  onClick={() => handleToggleReveal('claimmd')}
                  disabled={revealingService === 'claimmd'}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5 rounded transition-colors disabled:opacity-50"
                  title={showCmdPass ? 'Hide password' : 'Show / Reveal stored password'}
                >
                  {revealingService === 'claimmd' ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-500" />
                  ) : showCmdPass ? (
                    <EyeOff className="w-3.5 h-3.5 text-slate-600" />
                  ) : (
                    <Eye className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Advanced SFTP transport parameters toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvancedCmd(!showAdvancedCmd)}
              className="text-[10.5px] text-slate-600 hover:text-slate-900 flex items-center space-x-1 font-semibold cursor-pointer"
            >
              <span>{showAdvancedCmd ? 'Hide' : 'Show'} Transport SFTP Settings</span>
              {showAdvancedCmd ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {showAdvancedCmd && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-2 pt-2 border-t border-slate-200">
                <div>
                  <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    SFTP Host
                  </label>
                  <input
                    type="text"
                    value={cmdHost}
                    onChange={(e) => setCmdHost(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    SFTP Port
                  </label>
                  <input
                    type="number"
                    value={cmdPort}
                    onChange={(e) => setCmdPort(Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    Remote Inbound Path
                  </label>
                  <input
                    type="text"
                    value={cmdPath}
                    onChange={(e) => setCmdPath(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200">
            <div className="text-[10px] text-slate-600 font-mono">
              Route: <span className="text-emerald-700 font-bold">Claim.MD</span> (Active) • Submitter: <span className="text-amber-800 font-bold">21119</span> • Receiver: <span className="text-amber-800 font-bold">CLAIMMD</span> • Standby: Office Ally
            </div>

            <div className="flex items-center space-x-2">
              <button
                type="submit"
                id="btn-save-claimmd"
                disabled={savingKey === 'claimMd'}
                className="px-3 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
              >
                <Save className="w-3 h-3" />
                <span>{savingKey === 'claimMd' ? 'Saving...' : 'Save'}</span>
              </button>

              <button
                type="button"
                id="btn-test-claimmd"
                onClick={handleTestClaimMd}
                disabled={testingKey === 'claimMd'}
                className="px-2.5 py-1 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-medium flex items-center space-x-1.5 transition-all shadow-sm cursor-pointer disabled:opacity-50"
              >
                <CheckCircle2 className="w-3 h-3 text-slate-500" />
                <span>{testingKey === 'claimMd' ? 'Testing...' : 'Test Connection'}</span>
              </button>
            </div>
          </div>
        </form>
          </div>
        )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 5. COMPANY & BILLING IDENTITY */}
      {/* ========================================================================= */}
      <div className={`relative rounded-xl border transition-all overflow-hidden ${
        expandedSection === 'identity'
          ? 'border-slate-300 bg-white shadow-sm'
          : 'border-slate-200 hover:border-slate-300 bg-white shadow-sm'
      }`}>
        {/* Collapsed Header / Clickable Summary Row */}
        <div
          onClick={() => toggleSection('identity')}
          className="flex flex-wrap items-center justify-between gap-2.5 px-4 py-3 cursor-pointer select-none transition-colors"
        >
          <div className="flex items-center flex-wrap gap-2.5 min-w-0">
            <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10.5px] font-bold font-mono border border-slate-200 shrink-0">
              5
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900 text-xs tracking-tight">Company & Billing Identity</span>
              <span className="text-slate-600 text-xs font-normal">— EDI 837 Profile & Addresses</span>
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-2.5 ml-auto">
            <div className="hidden sm:flex items-center space-x-1.5 text-[11px] font-mono text-slate-600">
              <span>Org: <span className="text-slate-900 font-semibold">{identity.companyName || 'Allegiant Therapy LLC'}</span></span>
              <span className="text-slate-300">•</span>
              <span>NPI: <span className="text-slate-700 font-semibold">{identity.skilledNpi || identity.npi || 'Not Configured'}</span></span>
            </div>

            <span className="text-[9.5px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
              Live EDI Profile
            </span>

            <div className={`px-2 py-0.5 rounded text-[11px] font-semibold flex items-center space-x-1 border transition-all ${
              expandedSection === 'identity'
                ? 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900 hover:bg-slate-100'
            }`}>
              <span>{expandedSection === 'identity' ? 'Close' : 'Edit'}</span>
              {expandedSection === 'identity' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {expandedSection === 'identity' && (
          <div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-slate-50/40 space-y-3">

        <form onSubmit={handleSaveIdentity} className="space-y-3">
          {/* Row 1: Company Name, Legal Entity Name, Tax ID, Time Zone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Organization / Billing Name <span className="text-slate-400">*</span>
              </label>
              <input
                id="company-name-input"
                type="text"
                value={identity.companyName ?? ''}
                onChange={(e) => setIdentity({ ...identity, companyName: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Legal Entity Name <span className="text-slate-400">*</span>
              </label>
              <input
                id="legal-entity-name-input"
                type="text"
                value={identity.legalEntityName ?? ''}
                onChange={(e) => setIdentity({ ...identity, legalEntityName: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Federal Tax ID (EIN) <span className="text-amber-700 text-[9px] font-normal">(Hold until verified)</span>
              </label>
              <input
                id="company-tax-id-input"
                type="text"
                value={identity.taxId ?? ''}
                onChange={(e) => setIdentity({ ...identity, taxId: e.target.value })}
                placeholder="Unverified / Required for 837"
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 placeholder:text-slate-400 placeholder:font-sans"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Time Zone <span className="text-slate-400">*</span>
              </label>
              <input
                id="company-timezone-input"
                type="text"
                value={identity.timeZone ?? ''}
                onChange={(e) => setIdentity({ ...identity, timeZone: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
          </div>

          {/* Row 2: Billing Identifiers (Canonical Organization & Subparts) */}
          <div className="p-3 rounded-lg bg-white border border-slate-200 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center justify-between">
              <span>Billing Identifiers (Organization Type 2 & Provider Subparts)</span>
              <span className="text-[9.5px] text-slate-600 font-normal">Canonical Billing NPI: 1124738109</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              <div>
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  Organization Billing NPI (Type 2) <span className="text-slate-400">*</span>
                </label>
                <input
                  id="billing-organization-npi-input"
                  type="text"
                  value={identity.npi ?? ''}
                  onChange={(e) => setIdentity({ ...identity, npi: e.target.value })}
                  placeholder="1124738109"
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500"
                />
              </div>

              <div>
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  Taxonomy Code <span className="text-slate-400">*</span>
                </label>
                <input
                  id="billing-taxonomy-code-input"
                  type="text"
                  value={identity.taxonomyCode ?? ''}
                  onChange={(e) => setIdentity({ ...identity, taxonomyCode: e.target.value })}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500"
                />
              </div>

              <div>
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  Skilled AHCCCS Provider ID <span className="text-amber-700 text-[8.5px] font-normal">(Hold)</span>
                </label>
                <input
                  id="billing-skilled-ahcccs-input"
                  type="text"
                  value={identity.skilledAhcccsId ?? ''}
                  onChange={(e) => setIdentity({ ...identity, skilledAhcccsId: e.target.value, ahcccsId: e.target.value })}
                  placeholder="Unverified / Required for DDD"
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 placeholder:text-slate-400 placeholder:font-sans"
                />
              </div>

              <div>
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  Skilled Subpart NPI (Optional)
                </label>
                <input
                  id="billing-skilled-npi-input"
                  type="text"
                  value={identity.skilledNpi ?? ''}
                  onChange={(e) => setIdentity({ ...identity, skilledNpi: e.target.value })}
                  placeholder="Leave blank (uses Org NPI)"
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 placeholder:text-slate-400 placeholder:font-sans"
                />
              </div>

              <div>
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  Non-Skilled AHCCCS ID (Optional)
                </label>
                <input
                  id="billing-non-skilled-ahcccs-input"
                  type="text"
                  value={identity.nonSkilledAhcccsId ?? ''}
                  onChange={(e) => setIdentity({ ...identity, nonSkilledAhcccsId: e.target.value })}
                  placeholder="Optional / distinct ID"
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 placeholder:text-slate-400 placeholder:font-sans"
                />
              </div>
            </div>
          </div>

          {/* Row 3: Billing Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Billing Contact Name <span className="text-slate-400">*</span>
              </label>
              <input
                id="billing-contact-name-input"
                type="text"
                value={identity.billingContactName ?? ''}
                onChange={(e) => setIdentity({ ...identity, billingContactName: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Billing Contact Phone <span className="text-amber-700 text-[9px] font-normal">(Frozen NULL)</span>
              </label>
              <input
                id="billing-phone-input"
                type="text"
                value={identity.billingPhone ?? ''}
                onChange={(e) => setIdentity({ ...identity, billingPhone: e.target.value })}
                placeholder="Not configured (Fail-closed for 837)"
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 placeholder:text-slate-400"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Billing Contact Email <span className="text-slate-400">*</span>
              </label>
              <input
                id="billing-email-input"
                type="email"
                value={identity.billingEmail ?? ''}
                onChange={(e) => setIdentity({ ...identity, billingEmail: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
          </div>

          {/* Row 4: Physical Address (HIPAA Loop 2010AA - Non P.O. Box) */}
          <div className="p-3 rounded-lg bg-white border border-slate-200 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center justify-between">
              <span>Billing Provider Physical Street Address (Loop 2010AA)</span>
              <span className="text-[9.5px] text-amber-700 font-medium">HIPAA TR3: Physical address required (P.O. Box prohibited)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
              <div className="sm:col-span-6">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  Physical Street Address <span className="text-amber-700 text-[8.5px] font-normal">(Hold until verified)</span>
                </label>
                <input
                  id="billing-physical-address-input"
                  type="text"
                  value={identity.physicalAddress ?? ''}
                  onChange={(e) => setIdentity({ ...identity, physicalAddress: e.target.value })}
                  placeholder="Street address (Cannot be P.O. Box)"
                  className="w-full px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 focus:outline-none focus:border-slate-500 placeholder:text-slate-400"
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  City
                </label>
                <input
                  id="billing-physical-city-input"
                  type="text"
                  value={identity.physicalCity ?? ''}
                  onChange={(e) => setIdentity({ ...identity, physicalCity: e.target.value })}
                  placeholder="City"
                  className="w-full px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 focus:outline-none focus:border-slate-500 placeholder:text-slate-400"
                />
              </div>

              <div className="sm:col-span-1">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  State
                </label>
                <input
                  id="billing-physical-state-input"
                  type="text"
                  value={identity.physicalState ?? ''}
                  onChange={(e) => setIdentity({ ...identity, physicalState: e.target.value.toUpperCase() })}
                  maxLength={2}
                  placeholder="AZ"
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono uppercase focus:outline-none focus:border-slate-500 placeholder:text-slate-400"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  ZIP
                </label>
                <input
                  id="billing-physical-zip-input"
                  type="text"
                  value={identity.physicalZip ?? ''}
                  onChange={(e) => setIdentity({ ...identity, physicalZip: e.target.value })}
                  placeholder="ZIP Code"
                  className="w-full px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500 placeholder:text-slate-400"
                />
              </div>
            </div>
          </div>

          {/* Row 5: Correspondence / Pay-To Address (Loop 2010AB) */}
          <div className="p-3 rounded-lg bg-white border border-slate-200 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center justify-between">
              <span>Pay-To & Correspondence Address (Loop 2010AB)</span>
              <span className="text-[9.5px] text-slate-600 font-normal">P.O. Box permitted for Pay-To & Correspondence</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
              <div className="sm:col-span-6">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  Correspondence Address <span className="text-slate-400">*</span>
                </label>
                <input
                  id="billing-address-input"
                  type="text"
                  value={identity.billingContactAddress ?? ''}
                  onChange={(e) => setIdentity({ ...identity, billingContactAddress: e.target.value })}
                  className="w-full px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 focus:outline-none focus:border-slate-500"
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  City <span className="text-slate-400">*</span>
                </label>
                <input
                  id="billing-city-input"
                  type="text"
                  value={identity.city ?? ''}
                  onChange={(e) => setIdentity({ ...identity, city: e.target.value })}
                  className="w-full px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 focus:outline-none focus:border-slate-500"
                />
              </div>

              <div className="sm:col-span-1">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  State
                </label>
                <input
                  id="billing-state-input"
                  type="text"
                  value={identity.state ?? ''}
                  onChange={(e) => setIdentity({ ...identity, state: e.target.value.toUpperCase() })}
                  maxLength={2}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono uppercase focus:outline-none focus:border-slate-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[9.5px] font-semibold text-slate-600 uppercase tracking-wider mb-0.5">
                  ZIP Code
                </label>
                <input
                  id="billing-zip-input"
                  type="text"
                  value={identity.zip ?? ''}
                  onChange={(e) => setIdentity({ ...identity, zip: e.target.value })}
                  className="w-full px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-900 font-mono focus:outline-none focus:border-slate-500"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-slate-200">
            <button
              type="submit"
              id="btn-save-billing-identity"
              disabled={savingKey === 'identity'}
              className="px-3.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{savingKey === 'identity' ? 'Saving Identity...' : 'Save Billing Identity'}</span>
            </button>
          </div>
        </form>
          </div>
        )}
      </div>
    </div>
  );
};
